import { defaults, isEmpty } from 'lodash-es';
import template from './address.html';
import {
  ADDRESS_STATUS,
  ADDRESS_FIELDS,
  ADDRESS_ERRORS,
  ADDRESS_CLASSIFICATION,
} from './constants';
import { isAddressForced } from './address.utils';
import './address.global.css';

const { assign, keys } = Object;

// Order Matters Here! - These should be in order of precedence from top to bottom
const ERROR_MESSAGES = [
  {
    error: ADDRESS_ERRORS.DUPLICATE,
    message: 'Your new and old addresses must be different',
  },
  {
    error: ADDRESS_ERRORS.APT_NOT_FOUND,
    message: 'Apartment number is invalid',
  },
  {
    error: ADDRESS_ERRORS.APT_NEEDED,
    message: 'Address requires an apartment number',
  },
  { error: ADDRESS_ERRORS.INVALID, message: `We don't recognize this address` },
  {
    error: ADDRESS_ERRORS.COMMERCIAL_FROM,
    message: `
      USPS prohibits mail forwarding from a commercial address.
      <a target="_blank" href="//updater.zendesk.com/hc/en-us/articles/360001648412-My-address-is-classified-as-commercial-but-it-s-residential-Why-is-that-">
        Learn more
      </a>
    `,
  },
  {
    error: ADDRESS_ERRORS.COMMERCIAL_TO,
    message: `
      You cannot forward mail to a commercial address. If you believe this is a mistake, email <a href="mailto:help@updater.com">help@updater.com</a>.
    `,
  },
  {
    error: ADDRESS_ERRORS.MILITARY,
    message: `
      The USPS requires that military address changes be completed in-person at your local post office or on the USPS website.
      <a target="_blank" rel="noopener" href="https://moversguide.usps.com">
        Visit USPS website
      </a>
    `,
  },
  {
    error: ADDRESS_ERRORS.REQUIRED,
    message: `You have required fields missing`,
  },
];

const FORCEABLE = [
  ADDRESS_ERRORS.INVALID,
  ADDRESS_ERRORS.APT_NEEDED,
  ADDRESS_ERRORS.APT_NOT_FOUND,
];

const STATUS_TEXT = {
  [ADDRESS_STATUS.VALID]: 'Official USPS Address',
  [ADDRESS_STATUS.FORCED]: `Overridden Address`,
  [ADDRESS_STATUS.INVALID]: `Unrecognized Address`,
  [ADDRESS_STATUS.APT_NEEDED]: `Unrecognized Address`,
  [ADDRESS_CLASSIFICATION.COMMERCIAL]: 'Commercial Address',
  [ADDRESS_CLASSIFICATION.MILITARY]: 'Military Address',
};

class AddressController {
  constructor($timeout, updAddressVerifier, $sce) {
    'ngInject';
    Object.assign(this, {
      $timeout,
      _verifier: updAddressVerifier,
      trustAsHtml: $sce.trustAsHtml,
    });
  }

  $onInit() {
    defaults(this, {
      internalModel: {},
      model: {},
      editable: true,
      allowStatic: true,
      verifyForced: false,
      _editing: isEmpty(this.model),
    });

    this._onAddressChange(this.model);

    // $timeout needed to ensure $validators are set
    this.$timeout(() => {
      // pull out invalid from validators because we might want to validate forced
      const { invalid, ...validators } = this.modelCtrl.$validators;
      this._forced = isAddressForced(this.model)
        ? forceAllValidatorKeys(
            this.verifyForced ? validators : this.modelCtrl.$validators
          )
        : {};

      this._verify();
    });
  }

  $onChanges({ model }) {
    if (model) {
      this.internalModel = assign({}, this.internalModel, model.currentValue);
    }
  }

  get status() {
    const { classification, status } = this.internalModel || {};
    return status === ADDRESS_STATUS.FORCED
      ? STATUS_TEXT[ADDRESS_STATUS.FORCED]
      : STATUS_TEXT[classification] ||
          STATUS_TEXT[status] ||
          STATUS_TEXT[ADDRESS_ERRORS.INVALID];
  }

  get currentError() {
    return (
      ERROR_MESSAGES.find(({ error }) => this.modelCtrl.$error[error]) || {}
    );
  }

  get isEditing() {
    return this._editing || !this.allowStatic;
  }

  edit() {
    this._editing = this.editable;
  }

  set _forced(value) {
    this.modelCtrl._forced = value;
  }

  get _forced() {
    return this.modelCtrl._forced;
  }

  force(error) {
    this._forced = assign({}, this._forced, { [error]: true });

    // For commercial addresses we don't want to set the status to forced because it's valid.
    // However we need to acknowledge that the user is ok with using a commercial address so we
    // mark the form as forced and the address as valid.
    const status =
      error !== ADDRESS_ERRORS.COMMERCIAL_TO
        ? ADDRESS_STATUS.FORCED
        : ADDRESS_STATUS.VALID;
    this._onAddressChange({ status });
  }

  isForceable(error) {
    return FORCEABLE.includes(error);
  }

  onFormChange(fields) {
    // We clear the status, classification and forced validations
    // whenever the user changes the address so that we can
    // revalidate the address.
    this._forced = {};
    this._onAddressChange(fields, {
      status: null,
      classification: null,
      // Ensure that `street` always equals `base_street`.
      // TODO: Remove once base_street is not used anymore.
      street: fields[ADDRESS_FIELDS.BASE_STREET],
    });
    this._verify();
  }

  _setAddress(...args) {
    this.internalModel = assign({}, this.internalModel, ...args);
    this.modelCtrl.$setViewValue(this.internalModel);
  }

  _onAddressChange(...args) {
    this._setAddress(...args);
    this.change({ $value: this.internalModel });
  }

  _verify() {
    const success = result => {
      if (result) {
        this._onAddressChange(result);
      }
    };

    this._verifier.verify(this.internalModel, this.verifyForced).then(success);
  }
}

export const AddressComponent = {
  template,
  controller: AddressController,
  require: {
    modelCtrl: 'ngModel',
    formCtrl: '^^?form',
  },
  bindings: {
    model: '<ngModel',
    name: '@',
    label: '@',
    editable: '<?',
    verifyForced: '<?',
    allowStatic: '<?',
    requiredFields: '<updAddressRequired',
    change: '&',
  },
};

function forceAllValidatorKeys(validators) {
  return keys(validators).reduce(
    (ret, key) => assign(ret, { [key]: true }),
    {}
  );
}
